<!-- @format -->
<template>
	<template v-if="!selectField">
		<el-empty description="请从左侧拖拽字段" />
	</template>
	<template v-else>
		<div class="field-header">
			<span class="field-header-label">
				<el-icon v-if="checkFieldDetail.icon">
					<component :is="`${checkFieldDetail.icon}`" />
				</el-icon>
				{{ checkFieldDetail.label }}
			</span>
			<el-dropdown v-if="canChangeType" max-height="400" @command="onCheckType">
				<span class="check-dropdown-link">
					切换组件
					<el-icon class="el-icon--right">
						<arrow-down />
					</el-icon>
				</span>
				<template #dropdown>
					<el-dropdown-menu>
						<el-dropdown-item v-for="item in checkFieldList" :key="item.value" :command="item.value">
							{{ item.label }}
						</el-dropdown-item>
					</el-dropdown-menu>
				</template>
			</el-dropdown>
		</div>
		<el-form
			ref="ruleFormRef"
			:key="selectField!.code"
			:model="selectField!.pcProps"
			label-position="left"
			label-width="auto"
			size="small"
			class="setting-container-field"
		>
			<pc-collapse v-model="activeNames">
				<pc-collapse-item v-for="item in configGroups" :key="item.title" :title="item.title" :name="item.title">
					<template v-for="key in item.props" :key="key">
						<field-item :item-key="key" />
					</template>
				</pc-collapse-item>
			</pc-collapse>
		</el-form>
	</template>
</template>

<script setup lang="ts">
	import { useDragStoreWithOut } from '../../../store';
	import { useBaseConfigStoreWithOut } from '../../../config/widget';
	import { FieldsType, Container } from '../../../types';
	import { deepClone } from '../../../utils/util';
	import FieldItem from './item.vue';
	import type { FormInstance } from 'element-plus';
	import { PcCollapse, PcCollapseItem } from '@/components';

	interface Props {
		canChangeField?: boolean | ((a: any) => boolean);
	}

	const props = withDefaults(defineProps<Props>(), {});

	const emit = defineEmits(['on-attribute-change']);

	const name = inject<string>('name');

	const dragStore = useDragStoreWithOut(name);
	const baseConfigStore = useBaseConfigStoreWithOut(name);

	const ruleFormRef = ref<FormInstance>();

	const activeNames = ref(baseConfigStore.getFieldsAttributeWidget().map((i) => i.title));

	const selectField = computed({
		get() {
			return dragStore.selectField;
		},
		set(val) {
			dragStore.setState({ selectField: val });
		},
	});

	const configGroups = computed(() => {
		return selectField.value ? getConfigGroups(selectField.value) : [];
	});

	const canChangeType = computed(() =>
		typeof props.canChangeField === 'function' ? props.canChangeField(selectField.value) : props.canChangeField,
	);

	const fieldList = computed(() => {
		return baseConfigStore.FIELDS.reduce(
			(arr, item) => {
				item.children.forEach((itm) => {
					if (itm.category === 'container') {
						arr[0].push({ detail: itm, label: itm.pcProps.label, value: itm.type });
					} else {
						arr[1].push({ detail: itm, label: itm.pcProps.label, value: itm.type });
					}
				});
				return arr;
			},
			[[], []],
		);
	});

	const checkFieldList = computed(() =>
		selectField.value.category === 'container' ? fieldList.value[0] : fieldList.value[1],
	);

	const checkFieldDetail = computed(() => {
		const checkField = checkFieldList.value.find((itm) => itm.value === selectField.value.type);
		return {
			label: checkField?.label,
			icon: checkField?.detail?.icon,
		};
	});

	const onCheckType = (type) => {
		const field = checkFieldList.value.find((itm) => itm.value === type);
		const newField: FieldsType = deepClone(field.detail) as FieldsType;

		for (const key in newField.pcProps) {
			if (Object.prototype.hasOwnProperty.call(selectField.value.pcProps, key)) {
				newField.pcProps[key] = selectField.value.pcProps[key];
			}
		}

		// tabs层级和其他组件不一样，需要单独处理
		// 其他组件 => tabs, 处理children
		if (selectField.value.category === 'container' && type === 'tabs') {
			const newChildren: any = (newField as Container).children;
			newChildren[0].children = deepClone(selectField.value.children);
			selectField.value.children = newChildren;
		} else if (selectField.value.type === 'tabs') {
			// tabs => 其他组件
			const newChildren = deepClone(
				selectField.value.children.reduce((arr, item) => {
					return [...arr, ...item.children];
				}, []),
			);
			selectField.value.children = newChildren;
		}
		//  最后处理type和pcProps属性
		selectField.value.type = type;
		selectField.value.pcProps = newField.pcProps;
	};

	const getConfigGroups = (item: Record<string, any>) => {
		const fieldItemPcProps =
			[...fieldList.value[0], ...fieldList.value[1]].find((it) => it.detail.type === item.type)?.detail?.pcProps || {};

		return baseConfigStore.getFieldsAttributeWidget().reduce((arr, itm) => {
			const props = itm.props.filter((it) => Object.prototype.hasOwnProperty.call(fieldItemPcProps, it));
			if (props.length) {
				return [...arr, { ...itm, props }];
			}
			return arr;
		}, []);
	};

	// isPassword为false时设置showPassword为false
	watch(
		() => selectField.value?.pcProps?.isPassword,
		(newVal) => {
			if (newVal === false) {
				selectField.value!.pcProps.showPassword = false;
			}
		},
	);

	// 多选为false时multipleLimit为0，清空默认值
	watch(
		() => selectField.value?.pcProps?.multiple,
		(newVal) => {
			if (newVal === false) {
				if (Object.prototype.hasOwnProperty.call(selectField.value!.pcProps, 'multipleLimit')) {
					selectField.value!.pcProps.multipleLimit = 0;
				}
				if (Object.prototype.hasOwnProperty.call(selectField.value!.pcProps, 'defaultValue')) {
					selectField.value!.pcProps.defaultValue = '';
				}
			}
		},
	);

	// max必须大于min
	watch([() => selectField.value?.pcProps?.max, () => selectField.value?.pcProps?.min], ([newMax, newMin]) => {
		if (
			Object.prototype.hasOwnProperty.call(selectField.value?.pcProps, 'max') &&
			Object.prototype.hasOwnProperty.call(selectField.value?.pcProps, 'min') &&
			newMax < newMin
		) {
			nextTick(() => {
				selectField.value!.pcProps.max = newMin;
			});
		}
	});

	watch(
		[() => selectField.value?.code, () => selectField.value?.pcProps],
		([newCode, _newPcProps], [oldCode, _oldPcProps]) => {
			// code变证明是切换字段或新增
			if (newCode !== oldCode) {
				nextTick(() => {
					ruleFormRef.value?.validate();
				});
			}
			// code不变则为当前属性变化
			else {
				emit('on-attribute-change', selectField.value);
			}
		},
		{
			deep: true,
		},
	);
</script>

<style scoped lang="scss">
	.field-header {
		display: flex;
		justify-content: space-between;
		padding: 0 5px 10px;
		font-size: var(--el-font-size-base);
		font-weight: 500;

		.field-header-label {
			display: flex;
			align-items: center;
			gap: 5px;
		}
	}

	.check-dropdown-link {
		cursor: pointer;
		color: var(--el-color-primary);
		display: flex;
		align-items: center;
	}

	.setting-container-field {
		:deep(.el-form-item__label) {
			font-size: 13px;
		}
	}
</style>
